import React, { useState, useEffect, useCallback, useRef } from "react";
import MeditationTracker from "./MeditationTracker";
import DayNavigator from "../../components/DayNavigator";
import WeeklyLog from "./WeeklyLog";
import { syncLogToShopify } from "../../utils/syncLogToShopify";
import { launchConfetti } from "../../utils/confetti";
import { calculateStreak } from '../../utils/calculateStreak';

// Helper to parse a "YYYY-MM-DD" string as a local Date
const parseLocalDateString = (str) => {
  const [year, month, day] = str.split("-").map(Number);
  return new Date(year, month - 1, day);
};

const toDatetimeLocal = (isoString) => {
  if (!isoString || typeof isoString !== "string") return "";
  const date = new Date(isoString);
  if (isNaN(date.getTime())) return "";
  const tzOffset = date.getTimezoneOffset() * 60000;
  return new Date(date - tzOffset).toISOString().slice(0, 16);
};

const getDateStrFromDate = (date) => {
  const local = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return local.toISOString().split("T")[0];
};

const DailyMeditationLogger = ({ streak, setStreak, currentScreen }) => {
  // Format a Date object as "YYYY-MM-DD" in local time
  const formatLocalDate = (date) =>
    `${date.getFullYear()}-${(date.getMonth() + 1)
      .toString()
      .padStart(2, "0")}-${date.getDate().toString().padStart(2, "0")}`;
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dayNumber, setDayNumber] = useState(1);
  const [log, setLog] = useState(() => {
    try {
      const stored = localStorage.getItem("custom.hyman_hive_meditation_log");
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error("Error parsing meditation log from localStorage", e);
      return {};
    }
  });

  // Add local state for saving log
  const [savingLog, setSavingLog] = useState(false);

  // Store startDate in state to avoid repeated localStorage access
  const [startDate, setStartDate] = useState(() => {
    return localStorage.getItem("custom.hyman_hive_meditation_start_date");
  });

  // Fallback logic for startDate: if not set, use the date of the first log entry,
  // or the currently selected date if none exists.
  useEffect(() => {
    if (!startDate) {
      const firstLoggedDay = Object.values(log)[0];
      const firstDate = firstLoggedDay?.date;
      if (firstDate) {
        const dateStr = getDateStrFromDate(selectedDate);

        localStorage.setItem("custom.hyman_hive_meditation_start_date", dateStr);
        setStartDate(dateStr);
        syncLogToShopify(
          "single_line_text_field",
          dateStr,
          "hyman_hive_meditation_start_date"
        );
      }
    }
  }, [log, startDate, selectedDate]);

  const getDayNumberFromDate = (selectedDate) => {
    if (!startDate) return 1;
    const msPerDay = 1000 * 60 * 60 * 24;
    const start = parseLocalDateString(startDate);
    // Normalize selectedDate to local date (year, month, day)
    const selected = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate()
    );
    return Math.floor((selected - start) / msPerDay) + 1;
  };

  useEffect(() => {
    if (selectedDate && startDate) {
      const newDayNumber = getDayNumberFromDate(selectedDate);
      setDayNumber(newDayNumber);
    }
  }, [selectedDate, startDate]);

  const updateMeal = useCallback(
    (updatedEntries) => {
      const dayKey = `Day ${dayNumber}`;
      const newLog = { ...log };

      const dateStr = getDateStrFromDate(selectedDate);

      if (!newLog[dayKey]) {
        newLog[dayKey] = {
          date: dateStr,
        };
      }
      delete newLog[dayKey].entries;
      newLog[dayKey] = {
        ...newLog[dayKey],
        ...updatedEntries,
      };

      setLog(newLog);
      localStorage.setItem(
        "custom.hyman_hive_meditation_log",
        JSON.stringify(newLog)
      );
    },
    [dayNumber, log, selectedDate]
  );
  // Compute current streak based on consecutive days logged from startDate
  useEffect(() => {
    if (currentScreen === "meditation") {
      setStreak(calculateStreak(log));
    }
  }, [log, currentScreen]);

  const dayKey = `Day ${getDayNumberFromDate(selectedDate)}`;
  const dayLog = log[dayKey] || {};

  return (
    <div className="tw-flex tw-gap-6 tw-flex-start tw-justify-start tw-flex-col-reverse lg:tw-flex-row">
      <div className="tw-w-full lg:tw-2/3 daily-log">
        <div className="tw-bg-white  dark:tw-bg-black tw-px-3 sm:tw-px-10  tw-py-4 lg:tw-py-8 tw-rounded-2xl tw-shadow-sm tw-w-full tw-h-full">
          <div className="welcome tw-border-b tw-pb-7 tw-flex tw-flex-col lg:tw-flex-row tw-items-start lg:tw-items-center tw-justify-between dark:tw-border-gray-700">
            <DayNavigator
              selectedDate={selectedDate}
              setSelectedDate={setSelectedDate}
              dayNumber={dayNumber}
              startDate={startDate}
            />
          </div>

          <div className="tw-relative tw-mt-6">
            <MeditationTracker
              logEntry={dayLog}
              onChange={(updatedEntries) => updateMeal(updatedEntries)}
              isVisible={true}
            />

            {dayNumber % 7 === 0 && (
              <div className="tw-mt-6 tw-space-y-4 tw-mx-4 tw-border-t tw-py-6">
                <h2 className="tw-text-lg tw-font-semibold">
                  Weekly Reflection
                </h2>
                <div className="tw-mb-4">
                  <label className="tw-block tw-font-medium tw-mb-1">
                    What patterns are you noticing in your mood or mindset after
                    meditation?
                  </label>
                  <textarea
                    rows={2}
                    className="tw-w-full tw-border tw-rounded-lg tw-p-4 dark:tw-bg-black"
                    value={dayLog["Weekly Reflection 1"] || ""}
                    onChange={(e) =>
                      updateMeal({
                        ...dayLog,
                        ["Weekly Reflection 1"]: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="tw-flex tw-flex-wrap tw-gap-4">
                  {[
                    "Any surprising insights or recurring thoughts?",
                    "Have you noticed changes in sleep, stress levels, or daily resilience?",
                  ].map((q, i) => (
                    <div
                      className="tw-w-full lg:tw-w-[calc(50%-0.5rem)]"
                      key={i}
                    >
                      <label className="tw-block tw-font-medium tw-mb-1">
                        {q}
                      </label>
                      <textarea
                        rows={2}
                        className="tw-w-full tw-border tw-rounded-lg tw-p-4 dark:tw-bg-black"
                        value={dayLog[`Weekly Reflection ${i + 2}`] || ""}
                        onChange={(e) =>
                          updateMeal({
                            ...dayLog,
                            [`Weekly Reflection ${i + 2}`]: e.target.value,
                          })
                        }
                      />
                    </div>
                  ))}
                </div>

                {[
                  "What's been your biggest challenge in sticking with the practice?",
                  "What adjustments might help you deepen or improve your experience next week?",
                ].map((q, i) => (
                  <div className="tw-w-full" key={i}>
                    <label className="tw-block tw-font-medium tw-mb-1">
                      {q}
                    </label>
                    <textarea
                      className="tw-w-full tw-border tw-rounded-lg tw-p-4 dark:tw-bg-black"
                      value={dayLog[`Weekly Reflection ${i + 2}`] || ""}
                      onChange={(e) =>
                        updateMeal({
                          ...dayLog,
                          [`Weekly Reflection ${i + 2}`]: e.target.value,
                        })
                      }
                    />
                  </div>
                ))}
              </div>
            )}

            {dayNumber % 30 === 0 && (
              <div className="tw-mt-6 tw-space-y-4 tw-mx-4 tw-border-t tw-py-6">
                <h2 className="tw-text-lg tw-font-semibold">
                  End-of-Challenge Reflection
                </h2>
                <div className="tw-mb-4">
                  <div className="tw-flex tw-flex-wrap tw-gap-4">
                    {[
                      "What was the most noticeable change you experienced after this month's Meditation Challenge?",
                      "Did your relationship with stress shift in any way?",
                    ].map((q, i) => (
                      <div className="tw-w-full" key={i}>
                        <label className="tw-block tw-font-medium tw-mb-1">
                          {q}
                        </label>
                        <textarea
                          rows={2}
                          className="tw-w-full tw-border tw-rounded-lg tw-p-4 dark:tw-bg-black"
                          value={dayLog[`Weekly Reflection ${i + 2}`] || ""}
                          onChange={(e) =>
                            updateMeal({
                              ...dayLog,
                              [`Weekly Reflection ${i + 2}`]: e.target.value,
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div className="tw-flex tw-flex-wrap tw-gap-4 tw-mt-3">
                    {[
                      "What's one thing you want to carry forward from this experience?",
                      "Do you plan to continue meditating? Why or why not?",
                    ].map((q, i) => (
                      <div
                        className="tw-w-full lg:tw-w-[calc(50%-0.5rem)]"
                        key={i}
                      >
                        <label className="tw-block tw-font-medium tw-mb-1">
                          {q}
                        </label>
                        <textarea
                          rows={2}
                          className="tw-w-full tw-border tw-rounded-lg tw-p-4 dark:tw-bg-black"
                          value={dayLog[`Weekly Reflection ${i + 2}`] || ""}
                          onChange={(e) =>
                            updateMeal({
                              ...dayLog,
                              [`Weekly Reflection ${i + 2}`]: e.target.value,
                            })
                          }
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="tw-relative tw-flex tw-justify-start tw-mb-8 tw-mx-4">
              <button
                id={`log-save-button`}
                disabled={Object.keys(dayLog).length === 0}
                onClick={() => {
                  setSavingLog(true);
                  let currentTimestamp = dayLog.Timestamp;

                  if (!currentTimestamp) {
                    const now = new Date();
                    currentTimestamp = now.toISOString();
                    updateMeal({
                      ...dayLog,
                      Timestamp: currentTimestamp,
                    });
                  }

                  gtag("event", "meditation_log_saved", {
                    timestamp_logged: !!currentTimestamp,
                  });

                  const canvas = document.querySelector(`#log-save-canvas`);
                  const button = document.querySelector(`#log-save-button`);
                  if (canvas && button) launchConfetti(canvas, button);

                  syncLogToShopify("json", log, "hyman_hive_meditation_log").then(
                    () => {
                      setTimeout(() => {
                        setSavingLog(false);
                      }, 2000);
                    }
                  );
                }}
                className={`tw-px-6 tw-py-3 tw-rounded-full tw-shadow-sm
                  ${
                    Object.keys(dayLog).length
                      ? savingLog
                        ? "tw-bg-gray-500 tw-text-white"
                        : "tw-bg-black hover:tw-bg-gray-700 tw-text-white"
                      : "tw-bg-gray-100 tw-text-gray-300"
                  }`}
              >
                {savingLog ? (
                  <span className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-loader tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-w-4 tw-h-4 tw-animate-spin" />
                    Saving...
                  </span>
                ) : (
                  `Save log`
                )}
              </button>

              <div className="tw-mx-4 tw-flex tw-items-center tw-gap-2">
                <input
                  type="datetime-local"
                  value={toDatetimeLocal(dayLog.Timestamp)}
                  onChange={(e) => {
                    const updatedTimestamp = new Date(
                      e.target.value
                    ).toISOString();
                    updateMeal({
                      ...dayLog,
                      Timestamp: updatedTimestamp,
                    });
                  }}
                />
              </div>

              <canvas
                id={`log-save-canvas`}
                style={{ transform: "translate(-25%, -25%)" }}
                className="tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-bottom-0 tw-pointer-events-none tw-z-[9999]"
              ></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DailyMeditationLogger;
